import React, { useMemo, useState } from 'react'
import { useQueryParams } from 'use-query-params'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ContentLayout } from '@app/layouts'
import { useNotification, usePaginationQueryParams, useTableSearch, useTableSetting } from '@app/hooks'
import { Show } from '@app/components/util'

import { deleteContractorsCluster, getContractorsCluster } from '@app/api/routes/contractors'

import { BUTTON_TYPE, SIZE } from '@app/constants'
import { CONTRACTORS_KEY } from '@app/constants/queryKeys'
import { PERMISSION } from '@app/constants/permission'
import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'

import { LntButton } from '@app/components/base'
import { ExtendedTable, PermissionWrapper, SubheaderToolbarDivider, TableSettingsToolbar } from '@app/components'

import { TopFilters } from './ui'
import { getColumns, adaptConnectionsContractorsTableData } from './lib'
import { topFiltersQuerySchema, columnsSearchSchema, tableSearchQuerySchema } from './configs'

import type { Key } from 'react'
import type { FiltersParams } from '@app/types/filter'
import type { GetContractorsClusterListsParams } from '@app/api/routes/contractors/types'

const columns = getColumns()

export const ConnectionsContractorsTab: React.FC = () => {
  const [selectedIds, setSelectedIds] = useState<Key[]>([])

  const { paginationParams, pagination, setPagination, resetCurrentPage } = usePaginationQueryParams()

  const [queryFilters, setQueryFilters] = useQueryParams(topFiltersQuerySchema)

  const notification = useNotification()
  const queryClient = useQueryClient()

  const connectionsContractorsTabParams = useMemo(
    () => ({ ...paginationParams, ...queryFilters }),
    [paginationParams, queryFilters]
  ) as GetContractorsClusterListsParams

  const { data: connectionsContractorsTableData, isLoading: isListsDataLoading } = useQuery({
    queryKey: [CONTRACTORS_KEY.contractorsClustersItems, connectionsContractorsTabParams],
    queryFn: async () => await getContractorsCluster(connectionsContractorsTabParams),
    select: contractorsClusterData => adaptConnectionsContractorsTableData(contractorsClusterData)
  })

  const deleteConnectionMutation = useMutation({
    mutationFn: deleteContractorsCluster,
    onSuccess: async () => {
      setSelectedIds([])
      resetCurrentPage()
      await queryClient.invalidateQueries({ queryKey: [CONTRACTORS_KEY.contractorsClustersItems] })

      notification.success({
        message: 'Связь удалена',
        description: 'Успешное удаление связи'
      })
    }
  })

  const handleFiltersChange = (filters: FiltersParams): void => {
    setQueryFilters(filters)
    resetCurrentPage()
  }

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleSelectedRowChange = (selectedKeys: Key[]): void => {
    setSelectedIds(selectedKeys)
  }

  const handleRemoveConnections = (): void => {
    const ids = selectedIds.map(String)

    deleteConnectionMutation.mutate({ ids })
  }

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.connectionsContractorsTab, columns)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredContractsContractorsDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: connectionsContractorsTableData?.dataSource,
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  const toolbar = [
    <Show
      key='view_change_copy-show'
      condition={ !!selectedIds.length }
    >
      <PermissionWrapper requiredPermissions={ [PERMISSION.configureMonitoringParameters] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ handleRemoveConnections }
        >
          Удалить
        </LntButton>

        <SubheaderToolbarDivider />
      </PermissionWrapper>
    </Show>,

    <TableSettingsToolbar
      key='table-settings-toolbar'
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  ]

  return (
    <ContentLayout>
      <TopFilters
        onSearch={ handleFiltersChange }
        initialFilters={ queryFilters as FiltersParams }
      />

      <ExtendedTable
        loading={ isListsDataLoading }
        columns={ columnsWithSearchTool }
        dataSource={ filteredContractsContractorsDataSource }
        toolbar={ toolbar }
        rowSelection={ {
          onChange: handleSelectedRowChange,
          selectedRowKeys: selectedIds
        } }
        pagination={ {
          ...pagination,
          total: connectionsContractorsTableData?.count,
          onChange: handlePaginationChange
        } }
      />
    </ContentLayout>
  )
}
